import {
  AI_LABEL_ASSETS,
  AI_LABEL_COLOUR_HEX,
  AI_LABEL_OPACITY_MAX,
  AI_LABEL_OPACITY_MIN,
  AI_LABEL_OPACITY_STEP,
  AiLabelColour,
  AiLabelType
} from './aiLabel.constants'
import { BEIGE_300, BEIGE_600, BLUE_600, BOX_SHADOW_XS, CORAL_600, ERROR_RED_500, GRAY_300, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { CheckCircleOutline, InfoOutlined } from '@mui/icons-material'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUISwitch } from 'components/common/MUISwitch'
import React from 'react'
import { SliderBar } from 'components/common/SliderBar'
import { useTranslation } from 'react-i18next'

export interface AiLabelSectionProps {
  showAiLabel: boolean
  onToggle: (enabled: boolean) => void
  labelType: AiLabelType
  onLabelTypeChange: (type: AiLabelType) => void
  labelText: string
  onLabelTextChange: (text: string) => void
  colour: AiLabelColour
  onColourChange: (colour: AiLabelColour) => void
  opacity: number
  onOpacityChange: (opacity: number) => void
  onResetPlacement: () => void
  isDisabled?: boolean
  /** Inside a collapsible section that already shows the title and the switch ("Combine tools") */
  hideHeader?: boolean
}

/**
 * AiLabelSection — the "AI label" section, offered in every Photo Studio tool (EU AI Act Art. 50(4)).
 *
 * Toggle off — shows the AI Act reminder note with a "Learn more" link (state A of the epic spec).
 * Toggle on — the note disappears and the client picks a label: AI badge, AI MODIFIED badge, or free text.
 * The selected label renders live on the preview (AiLabelOverlay) and is burned in on save (applyAiLabelToImage).
 *
 * @example
 * <AiLabelSection
 *   showAiLabel={aiLabelState.showAiLabel}
 *   onToggle={setAiLabelToggle}
 *   labelType={aiLabelState.labelType}
 *   onLabelTypeChange={setAiLabelType}
 *   labelText={aiLabelState.labelText}
 *   onLabelTextChange={setAiLabelText}
 *   isDisabled={isProcessing}
 * />
 */
export const AiLabelSection: React.FC<AiLabelSectionProps> = ({
  showAiLabel,
  onToggle,
  labelType,
  onLabelTypeChange,
  labelText,
  onLabelTextChange,
  colour,
  onColourChange,
  opacity,
  onOpacityChange,
  onResetPlacement,
  isDisabled = false,
  hideHeader = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])

  const handleToggle = () => {
    if (!isDisabled) {
      onToggle(!showAiLabel)
    }
  }

  const handleTypeSelect = (type: AiLabelType) => {
    if (!isDisabled) {
      onLabelTypeChange(type)
    }
  }

  const segmentSx = (isSelected: boolean) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '3.2rem',
    border: 'none',
    borderRadius: '0.4rem',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    backgroundColor: isSelected ? WHITE : 'transparent',
    boxShadow: isSelected ? BOX_SHADOW_XS : 'none',
    padding: '0 0.8rem',
  })

  // Badge glyphs are black SVGs: full opacity when selected, faded to read as gray otherwise
  const glyphSx = (isSelected: boolean, height: string) => ({
    height,
    opacity: isSelected ? 1 : 0.45,
    transition: 'opacity 0.15s ease',
  })

  // Rounded-square colour chips, styled like the Paint walls colour options (WallColorPicker)
  const colourChipSx = (chip: AiLabelColour) => ({
    position: 'relative',
    width: '3.17rem',
    height: '3.2rem',
    borderRadius: '0.8rem',
    backgroundColor: AI_LABEL_COLOUR_HEX[chip],
    // The white chip keeps a hairline so it stays visible on the panel background
    border: `1px solid ${colour === chip ? GRAY_900 : chip === 'white' ? GRAY_400 : 'transparent'}`,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.6 : 1,
    transition: 'all 0.2s ease-in-out',
    padding: 0,
    '&:hover': !isDisabled ? { borderColor: GRAY_900 } : {},
  })


  return (
    // Header-less: the section's own header sits above, so the first block loses its top margin
    <Box sx={{ width: '100%', ...(hideHeader ? { '& > :first-of-type': { marginTop: 0 } } : {}) }}>

      {!hideHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >

          <Typography
            sx={{
              fontSize: '1.6rem',
              fontWeight: 550,
              color: GRAY_900,
              lineHeight: 1.5,
              letterSpacing: '-0.64px',
            }}
          >
            {t('ai_label.title')}
          </Typography>

          <MUISwitch
            checked={showAiLabel}
            onChange={handleToggle}
            disabled={isDisabled}
            size="medium"
          />

        </Box>
      )}

      {/* Reminder note (EU AI Act) - only while the label toggle is off */}
      {!showAiLabel && (
        <Box sx={{ marginTop: '1.6rem', width: '100%' }}>
          <BorderBoxWrapper
            gap={0.2}
            padding={1.2}
            borderColor={BEIGE_600}
            backgroundColor={BEIGE_300}
            sx={{
              borderRadius: '0.8rem',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Stack direction="row" gap={0.8} sx={{ width: '100%', maxWidth: '100%' }}>

              <InfoOutlined sx={{ fontSize: '1.6rem', color: GRAY_700, flexShrink: 0, marginTop: '0.1rem' }} />

              <Typography
                variant="text-xs"
                fontWeight={350}
                color={GRAY_700}
                sx={{
                  lineHeight: '1.4',
                  letterSpacing: '-0.56px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                }}
              >
                {t('ai_label.note')}{' '}
                {/* Per-language blog slugs (final; IT falls back to the EN canonical - no IT article planned).
                    Scheme-guarded: translations load at runtime from the backend, so an anchor href is
                    a sink we must not trust blindly. */}
                <Typography
                  component="a"
                  href={/^https:\/\//.test(t('ai_label.learn_more_url')) ? t('ai_label.learn_more_url') : 'https://www.bkbn.com/blog'}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text-xs"
                  fontWeight={550}
                  sx={{ color: CORAL_600, textDecoration: 'none' }}
                >
                  {t('ai_label.learn_more')}
                </Typography>
              </Typography>

            </Stack>
          </BorderBoxWrapper>
        </Box>
      )}

      {/* Label type selection - only while the label toggle is on */}
      {showAiLabel && (
        <Stack
          direction="row"
          sx={{
            marginTop: '1.6rem',
            backgroundColor: GRAY_300,
            borderRadius: '0.6rem',
            padding: '0.2rem',
            gap: '0.2rem',
          }}
        >

          <Box
            component="button"
            type="button"
            onClick={() => handleTypeSelect(AiLabelType.AI)}
            disabled={isDisabled}
            aria-pressed={labelType === AiLabelType.AI}
            sx={segmentSx(labelType === AiLabelType.AI)}
          >
            <Box
              component="img"
              src={AI_LABEL_ASSETS[AiLabelType.AI].black}
              alt={t('ai_label.type_ai')}
              sx={glyphSx(labelType === AiLabelType.AI, '2rem')}
            />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => handleTypeSelect(AiLabelType.AI_MODIFIED)}
            disabled={isDisabled}
            aria-pressed={labelType === AiLabelType.AI_MODIFIED}
            sx={segmentSx(labelType === AiLabelType.AI_MODIFIED)}
          >
            <Box
              component="img"
              src={AI_LABEL_ASSETS[AiLabelType.AI_MODIFIED].black}
              alt={t('ai_label.type_ai_modified')}
              sx={glyphSx(labelType === AiLabelType.AI_MODIFIED, '1.6rem')}
            />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => handleTypeSelect(AiLabelType.TEXT)}
            disabled={isDisabled}
            aria-pressed={labelType === AiLabelType.TEXT}
            sx={segmentSx(labelType === AiLabelType.TEXT)}
          >
            <Typography
              variant="text-sm"
              fontWeight={500}
              color={labelType === AiLabelType.TEXT ? GRAY_900 : GRAY_700}
              sx={{ transition: 'color 0.15s ease' }}
            >
              {t('ai_label.type_text')}
            </Typography>
          </Box>

        </Stack>
      )}

      {/* Free text input - only for the Text label type */}
      {showAiLabel && labelType === AiLabelType.TEXT && (
        <Box
          sx={{
            marginTop: '1.6rem',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <MUIInputField
            value={labelText}
            onChange={(e) => onLabelTextChange(e.target.value)}
            placeholder={t('ai_label.text_placeholder')}
            disabled={isDisabled}
            maxLength={120}
            inputBoxWidth="100%"
            inputBoxHeight="4rem"
          />

          {/* Empty text would leave the image unlabelled - saving is blocked until fixed */}
          {!labelText.trim() && (
            <Typography variant="text-xs" sx={{ color: ERROR_RED_500, marginTop: '0.4rem', display: 'block' }}>
              {t('ai_label.text_required')}
            </Typography>
          )}
        </Box>
      )}

      {/* Colour - applies to all three label types */}
      {showAiLabel && (
        <Box
          sx={{
            marginTop: '1.6rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >

          <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>
            {t('ai_label.colour')}
          </Typography>

          <Stack direction="row" gap={0.8}>

            <Box
              component="button"
              type="button"
              onClick={() => !isDisabled && onColourChange('black')}
              disabled={isDisabled}
              aria-label={t('ai_label.colour_black')}
              aria-pressed={colour === 'black'}
              sx={colourChipSx('black')}
            >
              {colour === 'black' && <CheckCircleOutline sx={{ fontSize: '1.6rem', color: WHITE, display: 'block', margin: 'auto' }} />}
            </Box>

            <Box
              component="button"
              type="button"
              onClick={() => !isDisabled && onColourChange('white')}
              disabled={isDisabled}
              aria-label={t('ai_label.colour_white')}
              aria-pressed={colour === 'white'}
              sx={colourChipSx('white')}
            >
              {colour === 'white' && <CheckCircleOutline sx={{ fontSize: '1.6rem', color: GRAY_900, display: 'block', margin: 'auto' }} />}
            </Box>

          </Stack>

        </Box>
      )}

      {/* Opacity - applies to all three label types */}
      {showAiLabel && (
        <Box sx={{ marginTop: '1.2rem', width: '100%' }}>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >

            <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>
              {t('ai_label.opacity')}
            </Typography>

            <Typography variant="text-sm" color={GRAY_700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {opacity}%
            </Typography>

          </Box>

          {/* Thumb travel is inset while the rail/track extend back under it, so the thumb never crops at the panel clip edge */}
          <SliderBar
            value={opacity}
            min={AI_LABEL_OPACITY_MIN}
            max={AI_LABEL_OPACITY_MAX}
            step={AI_LABEL_OPACITY_STEP}
            marks={false}
            valueLabelDisplay="off"
            disabled={isDisabled}
            onChange={(_, value) => onOpacityChange(value as number)}
            aria-label={t('ai_label.opacity')}
            sx={{
              width: 'calc(100% - 28px)',
              maxWidth: 'calc(100% - 28px)',
              marginLeft: '14px',
              marginRight: '14px',
              paddingRight: 0,
              '& .MuiSlider-rail': {
                height: 4,
                background: GRAY_400,
                left: '-10px',
                width: 'calc(100% + 20px)',
                paddingLeft: 0,
              },
              '& .MuiSlider-track': {
                height: 4,
                background: BLUE_600,
                boxSizing: 'content-box',
                marginLeft: '-10px',
                paddingRight: '10px',
              },
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
                backgroundColor: WHITE,
                border: `1px solid ${GRAY_400}`,
                boxShadow: BOX_SHADOW_XS,
                '&:hover': {
                  boxShadow: BOX_SHADOW_XS,
                },
                '&.Mui-active, &.Mui-focusVisible': {
                  boxShadow: `${BOX_SHADOW_XS}, 0 0 0 1px ${BLUE_600}`,
                },
                '&::before': {
                  boxShadow: 'none',
                },
              },
            }}
          />

        </Box>
      )}

      {/* Placement - drag on the preview; reset restores the default corner.
          Hidden while an empty text label has nothing to drag */}
      {showAiLabel && (labelType !== AiLabelType.TEXT || !!labelText.trim()) && (
        <Box sx={{ marginTop: '1.2rem', width: '100%' }}>

          <Typography variant="text-xs" fontWeight={350} color={GRAY_700} sx={{ lineHeight: '1.4', display: 'block' }}>
            {t('ai_label.placement_hint')}
          </Typography>

          <Box
            component="button"
            type="button"
            onClick={() => !isDisabled && onResetPlacement()}
            disabled={isDisabled}
            sx={{
              marginTop: '0.8rem',
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: GRAY_700,
              fontSize: '1.3rem',
              textDecoration: 'underline',
            }}
          >
            {t('ai_label.reset_placement')}
          </Box>

        </Box>
      )}
    </Box>
  )
}
